/**
 * D-EZ-108 — THE RIVALRY BOARD (Career → Rivalries).
 *
 * Who has it in for whom, and why: the hottest rivalries with a heat meter, the
 * two drivers, how often each has finished ahead of the other, the first spark,
 * the worst moment and the last three things that happened between them; the
 * friendships and the veterans bringing young men along; and the rivalries that
 * have cooled or been buried. Every word is read off the rivalry book
 * (`domain/people/rivalry.ts`) — incidents the dice produced, nothing else.
 */

import { useMemo } from 'react';
import type { HistorySeason } from '../../domain/history/index.ts';
import {
  hottestRivalries,
  rivalryState,
  STATE_WORDS,
  type Bond,
  type RivalryMoment,
  type RivalryRecord,
} from '../../domain/people/index.ts';

export interface RivalryBoardProps {
  /** Every season of the league, oldest first (settled + the one being raced). */
  seasons: readonly HistorySeason[];
  onDriver?: (driverId: string) => void;
}

/** Heat at which the meter reads full. */
const METER_FULL = 36;

export function whenOf(m: Pick<RivalryMoment, 'seasonNumber' | 'raceNumber' | 'raceName' | 'trackName'>): string {
  if (m.raceNumber === 0) return `Winter before season ${m.seasonNumber}`;
  return `Season ${m.seasonNumber}, race ${m.raceNumber} · ${m.raceName}`;
}

function Who({ id, name, onDriver }: { id: string; name: string; onDriver?: (id: string) => void }) {
  return onDriver ? (
    <button type="button" className="linkbtn rivals__name" onClick={() => onDriver(id)}>
      {name}
    </button>
  ) : (
    <span className="rivals__name">{name}</span>
  );
}

/** "Head to head: Rasmussen ahead 49 times, Calloway 58, in 107 races together." Full names when the surnames match. */
export function headToHeadLine(r: Pick<RivalryRecord, 'aName' | 'bName' | 'headToHead'>): string {
  const last = (n: string) => n.split(' ').slice(-1)[0];
  const same = last(r.aName) === last(r.bName);
  const A = same ? r.aName : last(r.aName);
  const B = same ? r.bName : last(r.bName);
  const h = r.headToHead;
  return `Head to head: ${A} ahead ${h.aAhead} time${h.aAhead === 1 ? '' : 's'}, ${B} ${h.bAhead}, in ${h.races} race${h.races === 1 ? '' : 's'} together.`;
}

/** The meter is for rivalries still burning; a cooled or buried one shows none. */
export const BURNING: ReadonlySet<RivalryRecord['state']> = new Set(['boiling', 'hot', 'simmering']);

export function HeatMeter({ heat, state }: { heat: number; state: RivalryRecord['state'] }) {
  const pct = Math.max(4, Math.min(100, Math.round((heat / METER_FULL) * 100)));
  return (
    <div
      className={`heat heat--${state}`}
      role="meter"
      aria-label="Heat"
      aria-valuemin={0}
      aria-valuemax={METER_FULL}
      aria-valuenow={Math.round(heat)}
      aria-valuetext={`${STATE_WORDS[state]}, heat ${Math.round(heat)}`}
    >
      <span className="heat__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function RivalryCard({ r, onDriver }: { r: RivalryRecord; onDriver?: (id: string) => void }) {
  const heated = r.moments.filter((m) => m.kind !== 'hatchet');
  const last3 = heated.slice(-3).reverse();
  const h = r.headToHead;
  return (
    <li className={`rival rival--${r.state}`}>
      <div className="rival__top">
        <span className={`rival__state rival__state--${r.state}`}>{STATE_WORDS[r.state]}</span>
        {r.trend !== 'steady' && <span className="rival__trend">{r.trend === 'rising' ? 'getting worse' : 'cooling'}</span>}
        {r.teammates && <span className="rival__trend">teammates</span>}
      </div>
      <h3 className="rival__pair">
        <Who id={r.a} name={r.aName} onDriver={onDriver} /> <span className="rival__vs">v</span>{' '}
        <Who id={r.b} name={r.bName} onDriver={onDriver} />
      </h3>
      <HeatMeter heat={r.heat} state={r.state} />
      {h.races > 0 && <p className="rival__h2h">{headToHeadLine(r)}</p>}
      <dl className="rival__facts">
        {r.firstSpark && (
          <>
            <dt>First spark</dt>
            <dd>
              {r.firstSpark.text} <span className="rival__when">{whenOf(r.firstSpark)}</span>
            </dd>
          </>
        )}
        {r.worst && r.worst !== r.firstSpark && (
          <>
            <dt>Worst moment</dt>
            <dd>
              {r.worst.text} <span className="rival__when">{whenOf(r.worst)}</span>
            </dd>
          </>
        )}
      </dl>
      {last3.length > 0 && (
        <>
          <h4 className="rival__sub">Lately</h4>
          <ul className="rival__log">
            {last3.map((m, i) => (
              <li key={`${m.leagueRaceIndex}-${m.kind}-${i}`}>
                <span className="rival__when">{whenOf(m)}</span> {m.text}
              </li>
            ))}
          </ul>
        </>
      )}
    </li>
  );
}

function bondWords(b: Bond): string {
  if (b.kind === 'mentor') return `brought along by ${b.mentorId === b.a ? b.aName : b.bName}`;
  if (b.kind === 'friends') return `${b.sideBySide} finish${b.sideBySide === 1 ? '' : 'es'} side by side in the top ten`;
  return `${b.seasonsTogether.length} seasons in the same shop`;
}

export default function RivalryBoard({ seasons, onDriver }: RivalryBoardProps) {
  const book = useMemo(() => rivalryState({ archive: seasons }), [seasons]);
  const live = useMemo(() => hottestRivalries(book, 12), [book]);
  const past = useMemo(
    () =>
      book.rivalries
        .filter((r) => r.state === 'cooled' || r.state === 'buried-the-hatchet')
        .sort((x, y) => y.peak - x.peak)
        .slice(0, 12),
    [book],
  );
  const bonds = useMemo(() => book.bonds.filter((b) => b.current && b.kind !== 'teammates').slice(0, 10), [book]);

  return (
    <div className="rivals">
      <header className="rivals__head">
        <p className="rivals__kicker">The rivalry board</p>
        <h2>Who has it in for whom</h2>
        <p className="rivals__sub">
          Heat comes only from what happened on the track — wrecks, duels, feuds, finishes decided at the line. It cools a
          little after every race and a lot over the winter.
          {book.asOf && ` Through season ${book.asOf.seasonNumber}, race ${book.asOf.raceNumber}.`}
        </p>
      </header>

      {live.length === 0 ? (
        <p className="rivals__empty">Nobody has it in for anybody yet. Rivalries start on the track — give it a few races.</p>
      ) : (
        <ol className="rivals__list" aria-label="Rivalries, hottest first">
          {live.map((r) => (
            <RivalryCard key={`${r.a}|${r.b}`} r={r} onDriver={onDriver} />
          ))}
        </ol>
      )}

      {bonds.length > 0 && (
        <section className="rivals__bonds" aria-labelledby="rivals-bonds">
          <h3 id="rivals-bonds">Friends and mentors</h3>
          <ul>
            {bonds.map((b) => (
              <li key={`${b.a}|${b.b}`}>
                {b.kind === 'mentor' ? (
                  <>
                    <Who id={b.mentorId === b.a ? b.b : b.a} name={b.mentorId === b.a ? b.bName : b.aName} onDriver={onDriver} />{' '}
                    <span className="rival__when">— {bondWords(b)}</span>
                  </>
                ) : (
                  <>
                    <Who id={b.a} name={b.aName} onDriver={onDriver} /> and <Who id={b.b} name={b.bName} onDriver={onDriver} />{' '}
                    <span className="rival__when">— {bondWords(b)}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {past.length > 0 && (
        <section className="rivals__past" aria-labelledby="rivals-past">
          <h3 id="rivals-past">Cooled, buried and gone</h3>
          <ul>
            {past.map((r) => (
              <li key={`${r.a}|${r.b}`}>
                <span className={`rival__state rival__state--${r.state}`}>{STATE_WORDS[r.state]}</span>{' '}
                <Who id={r.a} name={r.aName} onDriver={onDriver} /> v <Who id={r.b} name={r.bName} onDriver={onDriver} />
                {r.worst && (
                  <span className="rivals__pastline">
                    {r.worst.text} <span className="rival__when">{whenOf(r.worst)}</span>
                  </span>
                )}
                {!r.bothActive && <span className="rivals__pastline">One of them has left the league.</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
